import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ErrorBoundary from '@components/ErrorBoundary';
import { CheckedIcon, UnCheckedIcon } from '@components/icon/svg';
import { EstimationSummary, LensCard, TranscriptCard } from '@components/interview/custom-analysis';
import CustomLensModal from '@components/modal/CustomLensModal';
import PageContainer from '@components/PageContainer';
import Spinner from '@components/spinner/Spinner';
import {
  CATEGORIES,
  CORE_LENSES,
  INITIAL_DISPLAY_COUNT,
  PREMIUM_LENSES,
} from '@constants/analysisLenses';
import { useEntitlementError } from '@context/EntitlementContext';
import { useMixpanel } from '@context/MixpanelProvider';
import {
  useFetchAssignedTags,
  useFetchTranscriptListPagination,
  useSingleInterview,
  useSingleInterviewStatus,
} from '@hooks/getQueries';
import { useRunAnalyzeInterview } from '@hooks/postQueries';
import { useCheckCredits } from '@hooks/useCheckCredits';
import usePermission from '@hooks/usePermission';
import { estimateCredits } from '@services/payment';
import { ANALYSIS_LENS_EVENTS, CREDITS_USED_EVENTS } from '@utils/eventSchema';

function InterviewCustomAnalysisContainer() {
  const { id } = useParams();
  const [filter, setFilter] = useState('');
  const { trackEvent } = useMixpanel();
  const { handleApiError } = useEntitlementError();
  const [dialogLens, setDialogLens] = useState(null);
  const [selectedTranscriptIds, setSelectedTranscriptIds] = useState([]);
  const [filteredTagList, setFilteredTagList] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedLens, setSelectedLens] = useState(null);
  const [showAllLenses, setShowAllLenses] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [openCustomLensModal, setOpenCustomLensModal] = useState(false);
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // MUI DataGrid is 0-based
    pageSize: 10,
  });

  // Memoize the API call parameters
  const queryParams = useMemo(
    () => ({
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    }),
    [paginationModel.page, paginationModel.pageSize],
  );

  const {
    data: transcriptsResponse,
    isPending: txLoading,
    isError: txError,
    refetch: refetchTranscripts,
  } = useFetchTranscriptListPagination(id, filteredTagList, queryParams, {}, 'completed');

  // Refetch when pagination or filters change
  useEffect(() => {
    refetchTranscripts();
  }, [queryParams, filteredTagList, refetchTranscripts]);

  const { data: assignedTagList } = useFetchAssignedTags(id, '');
  const [instructions, setInstructions] = useState('');
  const [isEnhancing, setIsEnhancing] = useState(false);
  const { mutate: runLensAnalysis, isPending: isProcessing } = useRunAnalyzeInterview(id);
  const { data: analysisStatus } = useSingleInterviewStatus(id);
  const { data: interview } = useSingleInterview(id);

  useEffect(() => {
    trackEvent('analysisLens', {
      ...ANALYSIS_LENS_EVENTS.LENS_PAGE_VIEWED,
      study_id: id,
    });
  }, []);

  const {
    data: { analysisPermission },
    isPending: permissionLoading,
  } = usePermission({
    study: interview,
  });

  const { NoCreditDialog } = useCheckCredits(null, true);

  // Unified entitlement estimation for ALL lenses (including grounded_emergence)
  // This replaces the previous dual-query approach where grounded_emergence
  // had a separate estimation path. Now all lenses use transcript-based analysis units.
  const { data } = useQuery({
    queryKey: ['estimate_credits_for_lens', id, selectedTranscriptIds],
    queryFn: () => {
      return estimateCredits({
        callType: 'qualitative_study_lens_analysis',
        studyId: id,
        transcriptIds: selectedTranscriptIds,
      });
    },
    enabled: !!id && selectedTranscriptIds.length > 0,
  });

  const handleRunAnalysis = async (lens) => {
    if (!lens) return;
    setSelectedLens(lens);
    setDialogLens(lens);
    setActiveStep(1);
  };

  const handleEstimationAgree = () => {
    if (!selectedLens) return;
    const trackingLens = dialogLens || selectedLens;
    // No processId needed - backend uses atomic entitlement deduction
    const payload = {
      id: id,
      lensType: selectedLens.id,
      transcriptIds: selectedTranscriptIds,
      custom_prompt: instructions?.trim() ? instructions : null,
      analysis_level: null,
    };

    runLensAnalysis(payload, {
      onSuccess: () => {
        trackEvent('analysisLens', {
          ...ANALYSIS_LENS_EVENTS.LENS_RUN_COMPLETED,
          study_id: id,
          lens_id: trackingLens?.id,
        });

        setSelectedTranscriptIds([]);

        setSelectedLens(null);
        setShowAllLenses(false);
        setSelectedCategories([]);
        setFilter('');
        navigate(`/interview/${id}/analyses`);
        queryClient.invalidateQueries({ queryKey: ['interview', id, 'analysis_status'] });
        queryClient.invalidateQueries({ queryKey: ['interview', id, 'analysis'] });
        queryClient.invalidateQueries({ queryKey: ['estimate_credits_for_lens', id] });

        trackEvent('creditsUsed', {
          ...CREDITS_USED_EVENTS.CREDITS_USED_ANALYSIS_LENS,
          study_id: id,
          lens_id: trackingLens?.id,
          lens_name: trackingLens?.name,
          lens_category: trackingLens?.category,
          creditsconsumed: data?.credits_required || 0,
        });
      },
      onError: (error) => {
        // Handle 402 insufficient entitlements error with modal
        handleApiError(error);

        trackEvent('analysisLens', {
          ...ANALYSIS_LENS_EVENTS.LENS_RUN_FAILED,
          study_id: id,
          lens_id: trackingLens?.id,
          error_message: error.message,
        });
      },
    });
  };

  const filteredLenses = useMemo(() => {
    // Combine both CORE_LENSES and PREMIUM_LENSES
    const allLenses = [...CORE_LENSES, ...PREMIUM_LENSES];

    return allLenses.filter((lens) => {
      const matchesFilter =
        lens.name.toLowerCase().includes(filter.toLowerCase()) ||
        lens.tags.some((tag) => tag.toLowerCase().includes(filter.toLowerCase()));
      const matchesCategory =
        selectedCategories.length === 0 || selectedCategories.includes(lens.category);
      return matchesFilter && matchesCategory;
    });
  }, [filter, selectedCategories]);

  // Filter and display logic
  const visibleLenses = useMemo(() => {
    let filtered = filteredLenses;
    if (!showAllLenses) {
      filtered = filtered.slice(0, INITIAL_DISPLAY_COUNT);
    }
    return filtered;
  }, [filteredLenses, showAllLenses]);

  const handleContinue = () => {
    setOpenCustomLensModal(false);
    setActiveStep(1);
  };

  const handleCloseCustomModal = () => {
    setOpenCustomLensModal(false);
    setInstructions('');
    setActiveStep(0);
    setSelectedLens(null);
  };

  const handleEnhancePrompt = async () => {
    setIsEnhancing(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (instructions.trim()) {
      setInstructions(
        (prev) =>
          `${prev}\n\nEnhanced suggestions:\n- Consider edge cases and error handling\n- Include comprehensive documentation\n- Follow best practices and design patterns`,
      );
    }
    setIsEnhancing(false);
  };

  const handleContinueToEstimate = () => {
    if (selectedTranscriptIds.length === 0) {
      console.warn('No transcripts selected for analysis');
      return;
    }

    setActiveStep(2);
  };

  // All lenses can be run multiple times with different transcript selections
  // No "already run" blocking - each run creates a new analysis item

  return (
    <PageContainer
      title="Interview | Custom Analysis (Lenses)"
      description="Run or view custom analyses"
    >
      {analysisPermission.runAnalysis || permissionLoading ? null : (
        <Alert severity="warning">
          You have no access to run lens since you have only view access to this study.
        </Alert>
      )}
      <Box maxWidth={'lg'} mx="auto" my={5} px={2}>
        <Typography variant="h4" fontWeight={600} gutterBottom textAlign="center" my={1}>
          Choose an Analysis Lens
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4, mt: 2 }}>
          Choose several analysis lenses. Each lens you run creates its own item in the{' '}
          <strong>Analysis</strong> tab, letting you inspect the data from multiple perspectives.
        </Typography>
        <Box
          sx={{
            width: '60%',
            mb: 4,
            mt: 2,
            mx: 'auto',
          }}
        >
          <Stepper activeStep={activeStep}>
            <Step
              sx={{
                '& .MuiStepLabel-label': { fontSize: '16px' },
                '& .MuiStepIcon-root': { fontSize: '32px' },
              }}
            >
              <StepLabel>Select Lens</StepLabel>
            </Step>
            <Step
              sx={{
                '& .MuiStepLabel-label': { fontSize: '16px' },
                '& .MuiStepIcon-root': { fontSize: '32px' },
              }}
            >
              <StepLabel>Select Transcripts</StepLabel>
            </Step>
            <Step
              sx={{
                '& .MuiStepLabel-label': { fontSize: '16px' },
                '& .MuiStepIcon-root': { fontSize: '32px' },
              }}
            >
              <StepLabel>Estimate Analysis</StepLabel>
            </Step>
          </Stepper>
        </Box>
        {/* Filter */}
        {activeStep === 0 && (
          <>
            {/* Search and Filter UI */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 3,
                gap: 2,
                justifyContent: 'space-between',
              }}
            >
              <TextField
                placeholder="Search analysis lenses..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                sx={{ flex: 1, maxWidth: 400, bgcolor: 'white' }}
              />
              <Link
                href="https://qualz.ai/docs/userguide/entitlements/"
                target="_blank"
                sx={{ textTransform: 'none' }}
              >
                How do entitlements work?
              </Link>
            </Box>

            {/* Collapsible Category Filters */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Filter by Category
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {CATEGORIES.map((category) => (
                  <Chip
                    key={category.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1 }}>
                        <Checkbox
                          checkedIcon={<CheckedIcon />}
                          icon={<UnCheckedIcon />}
                          checked={selectedCategories.includes(category.id)}
                        />
                        <Typography variant="body2">{category.label}</Typography>
                      </Box>
                    }
                    onClick={() => {
                      setSelectedCategories((prev) =>
                        prev.includes(category.id)
                          ? prev.filter((id) => id !== category.id)
                          : [...prev, category.id],
                      );
                    }}
                    sx={{
                      borderRadius: 0.5,
                      '& .MuiChip-label': { pl: 0 },
                    }}
                    variant={selectedCategories.includes(category.id) ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>
            </Box>
            {/* </Collapse> */}

            {/* Lens Grid */}
            <Grid container spacing={1}>
              {visibleLenses.map((lens) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={lens.id}>
                  <LensCard
                    lens={lens}
                    onRun={(lens) => {
                      handleRunAnalysis(lens);
                    }}
                    noTranscript={transcriptsResponse?.transcripts?.length === 0}
                    disabled={
                      !analysisPermission.runAnalysis ||
                      transcriptsResponse?.transcripts?.length === 0 ||
                      analysisStatus?.analysis_status == 'analysing'
                    }
                    selected={selectedLens?.id === lens.id}
                    onSelect={() => setSelectedLens(lens)}
                    setOpenCustomLensModal={setOpenCustomLensModal}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Load More Button */}
            {filteredLenses.length > INITIAL_DISPLAY_COUNT && (
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Button variant="outlined" onClick={() => setShowAllLenses(!showAllLenses)}>
                  {showAllLenses
                    ? 'Show Less'
                    : `Load ${filteredLenses.length - INITIAL_DISPLAY_COUNT} More`}
                </Button>
              </Box>
            )}

            {/* Continue Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                disabled={
                  !selectedLens ||
                  transcriptsResponse?.transcripts?.length === 0 ||
                  analysisStatus?.analysis_status == 'analysing' ||
                  selectedLens?.enterprise
                }
                onClick={() => setActiveStep(1)}
              >
                Continue
              </Button>
            </Box>
          </>
        )}

        {/* Step 2: Transcript Selection */}
        {activeStep === 1 && (
          <>
            <TranscriptCard
              selectedLens={selectedLens}
              txLoading={txLoading}
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
              transcriptsResponse={transcriptsResponse}
              txError={txError}
              assignedTagList={assignedTagList}
              setFilteredTagList={setFilteredTagList}
              id={id}
              setSelectedTranscriptIds={setSelectedTranscriptIds}
              selectedTranscriptIds={selectedTranscriptIds}
              setActiveStep={setActiveStep}
              handleContinueToEstimate={handleContinueToEstimate}
            />
          </>
        )}
        <NoCreditDialog />

        {activeStep === 2 && (
          <EstimationSummary
            selectedLens={selectedLens}
            selectedTranscriptIds={selectedTranscriptIds}
            setSelectedTranscriptIds={setSelectedTranscriptIds}
            data={data}
            setActiveStep={setActiveStep}
            handleEstimationAgree={handleEstimationAgree}
          />
        )}

        <CustomLensModal
          open={openCustomLensModal}
          onOpenChange={handleCloseCustomModal}
          handleContinue={handleContinue}
          instructions={instructions}
          setInstructions={setInstructions}
          isEnhancing={isEnhancing}
          handleEnhancePrompt={handleEnhancePrompt}
        />

        {isProcessing && (
          <Backdrop
            open={isProcessing}
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <Box display="flex" flexDirection="column" alignItems="center">
              <CircularProgress color="inherit" />
              <Typography mt={2}>Processing Analysis...</Typography>
            </Box>
          </Backdrop>
        )}
      </Box>
    </PageContainer>
  );
}

export default function InterviewCustomAnalysis() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<Spinner />}>
        <InterviewCustomAnalysisContainer />
      </Suspense>
    </ErrorBoundary>
  );
}
